import React, { useRef, useEffect, useCallback, useState } from 'react';

/**
 * Panel de firma con el dedo / ratón (canvas). Llama onChange(dataUrl|null)
 * cuando cambia la firma. `value` (data URL) permite restaurar la firma tras
 * validación del formulario o resize del viewport (tablet/teclado). (ACTA_FIRMA_PLAN.md v1)
 */
export default function SignaturePad({ onChange, value = null, height = 160 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const valueRef = useRef(value);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const restoreSignature = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) {
      setHasInk(false);
      return;
    }
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, rect.width, height);
      setHasInk(true);
    };
    img.onerror = () => setHasInk(false);
    img.src = dataUrl;
  }, [height]);

  // Ajusta el tamaño del canvas al contenedor (con devicePixelRatio para nitidez)
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    restoreSignature(valueRef.current);
  }, [height, restoreSignature]);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setupCanvas]);

  // Si el padre conserva la firma (p. ej. error de validación) pero el canvas se vació, restaurar.
  // Si value es null (cancelar / nuevo alta), vaciar el canvas.
  useEffect(() => {
    if (drawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!value) {
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      setHasInk(false);
      return;
    }
    restoreSignature(value);
  }, [value, restoreSignature]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div>
      <div className="relative rounded-lg border border-gray-300 bg-white" style={{ height }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair rounded-lg"
          style={{ touchAction: 'none' }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            Firma aquí con el dedo
          </span>
        )}
      </div>
      <div className="mt-1 flex justify-end">
        <button type="button" onClick={clear}
          className="text-xs text-gray-500 hover:text-gray-700 underline">
          Borrar firma
        </button>
      </div>
    </div>
  );
}
